import { RenderStateClippingPlane } from "@novorender/api";
import { vec3 } from "gl-matrix";
import { useEffect, useMemo, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useCameraState } from "contexts/cameraState";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectCrossSectionActivePlaneIdx } from "features/crossSection";
import { getCameraDir } from "features/engine2D/utils";
import { selectClippingPlanes, selectIsMobile, selectTerrain } from "features/render";
import { selectIsProcoreDesign } from "slices/explorer";
import { isCameraOnClippedSide } from "utils/math";

import { clippingOutlinesAreGpuComputed, clippingTrianglesAreDoubleSided } from "./clippingMobilePolicy";

const transparentColor = [0, 0, 0, 0] as const;

export function useHandleClipping() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const cameraState = useCameraState();
    const clipping = useAppSelector(selectClippingPlanes);
    const terrain = useAppSelector(selectTerrain);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const crossSectionActivePlaneIdx = useAppSelector(selectCrossSectionActivePlaneIdx);
    const isMobile = useAppSelector(selectIsMobile);

    const planeValuesPrev = useRef<RenderStateClippingPlane[]>([]);
    const planeValues = useMemo(() => {
        if (!view) {
            return [];
        }

        const cameraDir = getCameraDir(view.renderState.camera.rotation);
        const isOrtho = view.renderState.camera.kind === "orthographic";
        const cameraPos = cameraState?.position ?? view.renderState.camera.position;

        let changed = clipping.planes.length !== planeValuesPrev.current.length;

        const newPlanes = clipping.planes.map(({ baseW: _baseW, wBounds: _wBounds, ...plane }, idx) => {
            // If plane is parallel to camera in ortho mode - we'll just see whole screen tinted with plane color,
            // which doesn't really bring any value, so turn off plane color in this case
            const isParallelToCamera =
                isOrtho &&
                Math.abs(
                    vec3.dot(
                        cameraDir,
                        vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]),
                    ),
                ) > 0.999;

            const cameraIsOnClippedSide = isCameraOnClippedSide(cameraPos, plane.normalOffset);
            const blackOutline = isProcoreDesign && cameraIsOnClippedSide;
            const hideOutline = !cameraIsOnClippedSide && !isParallelToCamera && idx !== crossSectionActivePlaneIdx;
            const result: RenderStateClippingPlane = {
                normalOffset: plane.normalOffset,
                color: isParallelToCamera || !plane.showPlane ? transparentColor : plane.color,
                outline: {
                    enabled: !hideOutline && (blackOutline || (clipping.outlines && plane.outline.enabled)),
                    lineColor: blackOutline ? [0, 0, 0] : plane.outline.color,
                },
            };
            const prevResult = planeValuesPrev.current[idx];
            if (!prevResult || JSON.stringify(result) !== JSON.stringify(prevResult)) {
                changed = true;
            }
            return result;
        });

        if (!changed) {
            return planeValuesPrev.current;
        }
        return newPlanes;
    }, [view, clipping.planes, clipping.outlines, isProcoreDesign, crossSectionActivePlaneIdx, cameraState]);
    planeValuesPrev.current = planeValues;

    useEffect(() => {
        view?.modifyRenderState({
            clipping: {
                enabled: clipping.enabled,
                mode: clipping.mode,
                draw: true,
                gpuComputed: clippingOutlinesAreGpuComputed(isMobile, isProcoreDesign),
            },
        });
    }, [view, clipping.enabled, clipping.mode, isMobile, isProcoreDesign]);

    useEffect(() => {
        view?.modifyRenderState({ clipping: { planes: planeValues } });
    }, [view, planeValues]);

    const hasVisibleClippingPlanes = clipping.planes.some((p) => p.showPlane);
    useEffect(() => {
        view?.modifyRenderState({
            terrain: { asBackground: hasVisibleClippingPlanes ? false : terrain.asBackground },
        });
    }, [view, hasVisibleClippingPlanes, terrain]);

    const hasClippingPlanes = clipping.planes.length > 0;
    useEffect(() => {
        view?.modifyRenderState({
            triangles: { doubleSided: clippingTrianglesAreDoubleSided(hasClippingPlanes) },
        });
    }, [view, hasClippingPlanes]);
}
